import { memo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import {
  ContractorCardFilePreview,
  ContractorCardFileDropZone,
  ContractorCardFileUploadActions,
} from "../shared/components";
import { FileDragHandlers } from "@/hooks/useFileDragAndDrop";
import { CONTRACTOR_CARD_FILE_INPUT_ID } from "@/constants";
import { triggerFileInputClick } from "@/lib/fileManagerUtils";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { PreviewState } from "@/components/file-preview/hooks/useFilePreview";

interface FileSectionProps {
  pendingFile: { content: string; filename: string; mimetype: string } | null;
  savedFilename?: string;
  savedMimetype?: string;
  savedS3Key?: string;
  can_update: boolean;
  isDragging: boolean;
  isUploading: boolean;
  dragHandlers: FileDragHandlers;
  onViewFile: () => void;
  onCancelPending: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirmSend: () => void;
  previewDialog: PreviewState | null;
  onClosePreview: () => void;
}

export const FileSection = memo(
  ({
    pendingFile,
    savedFilename,
    savedMimetype,
    savedS3Key,
    can_update,
    isDragging,
    isUploading,
    dragHandlers,
    onViewFile,
    onCancelPending,
    onFileChange,
    onConfirmSend,
    previewDialog,
    onClosePreview,
  }: FileSectionProps) => {
    const { t } = useTranslation();

    const handleFileInputClick = useCallback(() => {
      triggerFileInputClick(CONTRACTOR_CARD_FILE_INPUT_ID);
    }, []);

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t("contractorCards.fields.card")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingFile || savedFilename ? (
            <ContractorCardFilePreview
              filename={pendingFile?.filename || savedFilename || ""}
              mimetype={pendingFile?.mimetype || savedMimetype || ""}
              isPending={!!pendingFile}
              isDragging={isDragging}
              dragHandlers={dragHandlers}
              s3Key={pendingFile ? undefined : savedS3Key}
              pendingFileContent={pendingFile?.content}
            />
          ) : (
            <ContractorCardFileDropZone
              isDragging={isDragging}
              dragHandlers={dragHandlers}
              onClick={handleFileInputClick}
            />
          )}

          <ContractorCardFileUploadActions
            hasFile={!!(pendingFile || savedFilename)}
            hasPendingFile={!!pendingFile}
            isUploading={isUploading}
            canUpdate={can_update}
            onFileChange={onFileChange}
            onConfirmSend={onConfirmSend}
            onViewFile={onViewFile}
            onCancelPending={onCancelPending}
          />

          {previewDialog && (
            <FilePreviewDialog
              open
              onClose={onClosePreview}
              fileUrl={previewDialog.fileUrl}
              fileName={previewDialog.fileName}
              mimeType={pendingFile?.mimetype || savedMimetype}
            />
          )}
        </CardContent>
      </Card>
    );
  },
);
